import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = useMemo(() => {
    return articles.find((a) => a.id === Number(id));
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-error-warning-line text-4xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '蘑菇影视',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: { '@id': 'https://mogutv.com/', name: '首页' },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: { name: article.title },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <Navbar />
      <main className="pt-16">
        <article className="px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mt-6">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mt-6 rounded-xl overflow-hidden bg-background-200">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 蘑菇影视`}
                className="w-full object-cover object-top aspect-video"
              />
            </div>

            <div className="mt-8 prose prose-lg max-w-none">
              {article.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2
                      key={index}
                      className="text-xl font-bold text-foreground-900 mt-8 mb-4"
                    >
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                if (paragraph.startsWith('### ')) {
                  return (
                    <h3
                      key={index}
                      className="text-lg font-semibold text-foreground-800 mt-6 mb-3"
                    >
                      {paragraph.replace('### ', '')}
                    </h3>
                  );
                }
                return (
                  <p
                    key={index}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                  >
                    {paragraph}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </div>
                    返回首页
                  </Link>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-wechat-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-weibo-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-link text-foreground-600"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((relArticle) => (
                  <ArticleCard key={relArticle.id} {...relArticle} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}